import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './ImageLightbox.css';

interface ImageLightboxProps {
  src: string;
  alt?: string;
  onClose: () => void;
  /** Revoke `src` (a blob: object URL the lightbox owns) when it closes. */
  revokeOnClose?: boolean;
}

/** Full-screen image viewer. Click the backdrop or press Esc to close. */
export function ImageLightbox({ src, alt, onClose, revokeOnClose }: ImageLightboxProps) {
  useEffect(() => {
    // Capture phase + stopPropagation so Esc dismisses ONLY the lightbox — the
    // thread view (EmailInlineView) and compose modal (useEscapeKey) also listen
    // for Escape on document/window and would otherwise close too.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // Revoke the owned blob URL on unmount (received-image case hands us a fresh URL).
  useEffect(() => {
    return () => {
      if (revokeOnClose && src.startsWith('blob:')) URL.revokeObjectURL(src);
    };
  }, [src, revokeOnClose]);

  return createPortal(
    <div className="email-lightbox-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <button className="email-lightbox-close" onClick={onClose} aria-label="Close" type="button">&times;</button>
      <img
        className="email-lightbox-img"
        src={src}
        alt={alt || ''}
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body,
  );
}
